import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Dismiss_DashExtensionUpsell } from "@mirage/analytics/events/types/dismiss_dash_extension_upsell";
import { PAP_Open_DashExtensionUpsell } from "@mirage/analytics/events/types/open_dash_extension_upsell";
import { PAP_Shown_DashExtensionUpsell } from "@mirage/analytics/events/types/shown_dash_extension_upsell";
import useAvailableExtensions from "@mirage/settings/utils/useAvailableExtensions";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import { ExtensionUpsellInfo } from "@mirage/shared/types";
import i18n from "@mirage/translations";
import { useCallback } from "react";
import styles from "./ExtensionUpsellBanner.module.css";

export const ExtensionUpsellBanner = ({
  extensionInfo,
  onDismiss,
  isEmbedded = false,
  maxWidth = "760px",
}: {
  extensionInfo: ExtensionUpsellInfo;
  onDismiss: () => void;
  isEmbedded?: boolean;
  maxWidth?: string;
}) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const { openExtensionUrl } = useAvailableExtensions();

  useDoOnceOnMount(() => {
    reportPapEvent(PAP_Shown_DashExtensionUpsell({}));
  });

  const onClick = useCallback(() => {
    reportPapEvent(PAP_Open_DashExtensionUpsell({}));
    openExtensionUrl();
  }, [openExtensionUrl, reportPapEvent]);

  const onDismissClicked = useCallback(() => {
    reportPapEvent(PAP_Dismiss_DashExtensionUpsell({}));
    onDismiss();
  }, [onDismiss, reportPapEvent]);

  const addExtensionButton = (
    <Button variant="primary" onClick={onClick}>
      {i18n.t("add_extension")}
    </Button>
  );

  return (
    <Box
      style={{ maxWidth }}
      width="100%"
      padding="12"
      backgroundColor="transparent"
    >
      <Box
        className={styles.extensionUpsellBanner}
        backgroundColor="Primary On Base"
      >
        <Split gap="10" alignY={isEmbedded ? "top" : "center"}>
          <Split.Item width="hug">{extensionInfo.iconLarge}</Split.Item>
          <Split.Item width="fill">
            <Text isBold tagName="div" style={{ marginBottom: "10px" }}>
              {i18n.t("extension_upsell_banner_title")}
            </Text>
            <Text tagName="div">
              {i18n.t("extension_upsell_banner_description")}
            </Text>
            {isEmbedded && <Box marginTop="10">{addExtensionButton}</Box>}
          </Split.Item>
          {!isEmbedded && (
            <Split.Item width="hug">
              <Box marginRight="20">{addExtensionButton}</Box>
            </Split.Item>
          )}
          <Split.Item>
            <IconButton variant="transparent" onClick={onDismissClicked}>
              <UIIcon src={CloseLine} />
            </IconButton>
          </Split.Item>
        </Split>
      </Box>
    </Box>
  );
};
